import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
// import { ScopeCertificateDetails } from '../../../sc-models/scope-certificate-details-dto';
import {
  ICertifiedOrganizationDetails,
  initialCertifiedOrganizationDetails,
  mapCertifiedOrganizationDetailsApiResponseToInitialValues,
  mapPayload,
} from "../../../sc-models/certified-organization-details-dto";
import FormBuilderV4 from "../../../../../../shared/components/form-builder/form-builder-v4";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
// import { API } from '../../../../../../shared/app-configs/sc-app-config';
import useNotification from "../../../../../../shared/hooks/useNotification";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { Box } from "@mui/material";
import { buyerSellerDetailsService } from "../../../../transaction-certificates/tc-service/buyer-seller-details.service";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { InnerButton } from "../../../../../../shared/components/buttons/floating-button-container";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { RestrictedDataValidation } from "../../../../../../shared/utils/custom-validation.utils";

interface CertifiedOrganizationDetailsProps {
  onNextTab: () => Promise<void>;
}

const CertifiedOrganizationDetails: React.FC<
  CertifiedOrganizationDetailsProps
> = ({ onNextTab }) => {
  const formRef = useRef<any>(null);
  const { loading, executeGet, setLoadingState, executePost } = useApi();

  const { notify } = useNotification();
  const navigate = useNavigate();
  const { formtype, SC_ID } = useParams();
  const hasCreateSC = formtype === "create-sc";
  const hasSubmitSC: boolean = formtype === "submited-sc";

  const [visible, setVisible] = useState({
    certifiedOrganizationTEId: true,
    certifiedOrganizationName: true,
    coTEIdLabel: true,
  });

  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [disable, setDisable] = useState({
    address1: true,
    country: true,
    state: true,
    town: true,
    postcode: true,
  });

  const getCountry = async () => {
    try {
              const result = await dtrackItServiceV4.getCountry();
      const countries = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1}  (${t.code_1})`,
      }));
      setCountryOptions(countries);
    } catch (err) {
      notify("Failed to fetch countries", "error");
    }
  };

  const getState = async (countryCode: string) => {
    try {
      setIsLoadingStates(true);
              const result = await dtrackItServiceV4.getStateBasedonCountry(countryCode);
      const states = result.data
        .filter((t: any) => !countryCode.includes(t.code_1)) // Do not include the country itself as a state
        .map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1}  (${t.code_1})`,
        }));
      setStateOptions(states);
    } catch (err) {
      setStateOptions([]);
    } finally {
      setIsLoadingStates(false);
    }
  };

  const [initialValues, setInitialValues] =
    useState<ICertifiedOrganizationDetails>(
      initialCertifiedOrganizationDetails
    );

  const fetchCoDetails = async () => {
    try {
      const response: any = await executeGet(API_ROUTES.GET_CO_DETAILS_V4, {
        scId: SC_ID,
      });
      Utills.cl("This is the response", response);
      const data: any =
        mapCertifiedOrganizationDetailsApiResponseToInitialValues(
          response.data
        );
      setInitialValues(data);

      // Move this check here to ensure it happens after data is loaded
      if (data.certifiedOrganizationTeId) {
        setVisible((prev) => ({
          ...prev,
          coTEIdLabel: false,
        }));
      }

      // Sync disable flags based on presence of values from API
      const isPresent = (v: any) => v !== null && v !== undefined && v !== "";
      const isTownPresent =
        data.certifiedOrganizationTown !== null &&
        data.certifiedOrganizationTown !== undefined &&
        data.certifiedOrganizationTown !== "" &&
        data.certifiedOrganizationTown !== "Not Available";
      setDisable({
        address1: isPresent(data.certifiedOrganizationAddress1),
        country: isPresent(data.certifiedOrganizationCountryOrArea),
        state: isPresent(data.certifiedOrganizationStateOrProvince),
        town: isTownPresent,
        postcode: isPresent(data.certifiedOrganizationPostcode),
      });

      // Ensure country code is formatted like TE handlers: "['IN']"
      if (data.certifiedOrganizationCountryOrArea) {
        const countryCode = JSON.stringify(
          `[${[data.certifiedOrganizationCountryOrArea].map((item: any) => `'${item}'`)}]`
        ).replace(/^"|"$/g, "");
        getState(countryCode);
      }
    } catch (err) {
      Utills.cl("I am in catch block");
      console.error("Failed to fetch CO Base Details:", err);
    }
  };

  useEffect(() => {
    getCountry();
    if (SC_ID || !hasCreateSC) {
      fetchCoDetails();
    }
  }, [SC_ID]);

  const fetchTEDetallsFromAPI = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      const data = response.data;
      Utills.cl(
        `fetchTEDetallsFromAPI details, query ${JSON.stringify(
          queryParams
        )} response data ${JSON.stringify(data)}`
      );

      return data;
    } catch (error) {
      console.error("Error fetching TE details:", error);
      throw error;
    }
  };

  const handleSellerTEIdValueChange = (value: any) => {
    const fields = [
      "certifiedOrganizationAddress1",
      "certifiedOrganizationTown",
      "certifiedOrganizationPostcode",
      "certifiedOrganizationStateOrProvince",
      "certifiedOrganizationCountryOrArea",
      "certifiedOrganizationName",
      "certifiedOrganizationTeId",
    ];
    if (value == null) {
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });
      setDisable((prev) => ({
        ...prev,
        country: true,
        state: true,
      }));
      setVisible((prev) => ({
        ...prev,
        // certifiedOrganizationName: true,
        coTEIdLabel: true,
      }));
    } else {
      formRef.current.setFieldValue(
        "certifiedOrganizationName",
        value.account_name
      );
      formRef.current.setFieldValue("certifiedOrganizationTeId", value.TE_ID);
      // formRef.current.setFieldValue(
      //   "certifiedOrganizationAddress1",
      //   value.street
      // );
      // Guard against null city — Yup.string() rejects null; coerce to empty string
      formRef.current.setFieldValue("certifiedOrganizationTown", value.city ?? "");
      formRef.current.setFieldValue(
        "certifiedOrganizationPostcode",
        value.postal_code
      );

      if (
        value.country != null &&
        value.country != "" &&
        value.country != undefined
      ) {
        let countryCode = JSON.stringify(
          `[${[value.country].map((item) => `'${item}'`)}]`
        ).replace(/^"|"$/g, "");
        getState(countryCode);
      }
      if (value.country == null) {
        setDisable((prev) => ({
          ...prev,
          country: false,
        }));
      } else {
        setDisable((prev) => ({
          ...prev,
          country: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationCountryOrArea",
          value.country
        );
      }

      if (value.street == null) {
        setDisable((prev) => ({
          ...prev,
          address1: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationAddress1", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          address1: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationAddress1",
          value.street
        );
      }

      if (value.state == null) {
        setDisable((prev) => ({
          ...prev,
          state: false,
        }));
      } else {
        setDisable((prev) => ({
          ...prev,
          state: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationStateOrProvince",
          value.state
        );
      }

      // Handle postcode
      if (value.postal_code != null) {
        setDisable((prev) => ({
          ...prev,
          postcode: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationPostcode",
          value.postal_code
        );
      } else {
        setDisable((prev) => ({
          ...prev,
          postcode: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationPostcode", "");
      }

      // Handle town
      if (value.city != null && value?.city !== 'Not Available') {
        setDisable((prev) => ({
          ...prev,
          town: true,
        }));
        formRef.current.setFieldValue("certifiedOrganizationTown", value.city);
      } else {
        setDisable((prev) => ({
          ...prev,
          town: false,
        }));
      }

      setVisible((prev) => ({
        ...prev,
        // certifiedOrganizationName: false,
        coTEIdLabel: false,
      }));
    }
  };

  const handleSellerNameValueChange = (value: any) => {
    const fields = [
      "certifiedOrganizationAddress1",
      "certifiedOrganizationTown",
      "certifiedOrganizationPostcode",
      "certifiedOrganizationStateOrProvince",
      "certifiedOrganizationCountryOrArea",
      "certifiedOrganizationName",
      "certifiedOrganizationTeId",
    ];
    if (value == null) {
      fields.forEach((field) => {
        formRef.current.setFieldValue(field, "");
      });

      setVisible((prev) => ({
        ...prev,
        certifiedOrganizationTEId: true,
      }));
      setDisable((prev) => ({
        ...prev,
        country: true,
        state: true,
      }));
    } else {
      formRef.current.setFieldValue(
        "certifiedOrganizationName",
        value.account_name
      );
      formRef.current.setFieldValue("certifiedOrganizationTeId", value.TE_ID);
      // formRef.current.setFieldValue(
      //   "certifiedOrganizationAddress1",
      //   value.street
      // );
      // Guard against null city — Yup.string() rejects null; coerce to empty string
      formRef.current.setFieldValue("certifiedOrganizationTown", value.city ?? "");
      formRef.current.setFieldValue(
        "certifiedOrganizationPostcode",
        value.postal_code
      );

      //handle certifiedOrganizationAddress1
      if (value.street === null ||
        value.street === "" ||
        value.street === undefined) {
        setDisable((prev) => ({
          ...prev,
          address1: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationAddress1", "");
      } else {
        setDisable((prev) => ({
          ...prev,
          address1: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationAddress1",
          value.street
        );
      }

      // Handle country
      if (
        value.country != null &&
        value.country != "" &&
        value.country != undefined
      ) {
        let countryCode = JSON.stringify(
          `[${[value.country].map((item) => `'${item}'`)}]`
        ).replace(/^"|"$/g, "");
        getState(countryCode);
        setDisable((prev) => ({
          ...prev,
          country: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationCountryOrArea",
          value.country
        );
      } else {
        setDisable((prev) => ({
          ...prev,
          country: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationCountryOrArea", "");
      }

      // Handle state
      if (value.state != null) {
        setDisable((prev) => ({
          ...prev,
          state: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationStateOrProvince",
          value.state
        );
      } else {
        setDisable((prev) => ({
          ...prev,
          state: false,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationStateOrProvince",
          ""
        );
      }

      // Handle postcode
      if (value.postal_code != null) {
        setDisable((prev) => ({
          ...prev,
          postcode: true,
        }));
        formRef.current.setFieldValue(
          "certifiedOrganizationPostcode",
          value.postal_code
        );
      } else {
        setDisable((prev) => ({
          ...prev,
          postcode: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationPostcode", "");
      }

      // Handle town
      if (value.city != null) {
        setDisable((prev) => ({
          ...prev,
          town: true,
        }));
        formRef.current.setFieldValue("certifiedOrganizationTown", value.city);
      } else {
        setDisable((prev) => ({
          ...prev,
          town: false,
        }));
        formRef.current.setFieldValue("certifiedOrganizationTown", "");
      }

      setVisible((prev) => ({
        ...prev,
        certifiedOrganizationTEId: false,
      }));
    }
  };

  const isNaLike = (v?: string | null) => {
    if (!v) return false;
    const lower = v.trim().toLowerCase();
    return ["na", "n/a", "na.", "na ", "not available", "null", "nil", "none", "n a", "n.a"].includes(lower);
  };

  const naOnlyValidator = (value?: string | null) => {
    if (!value) return true;
    const lower = value.trim().toLowerCase();
    if (!isNaLike(value)) return true;
    return lower === "na";
  };

  // Yup transform: silently normalize any NA variant → "na" before validation tests run.
  // This gives good UX — user can type "n/a", "N/A", "not available", "null" etc. and
  // the value is auto-corrected to "na" without showing a validation error.
  const normalizeNaVariant = (value: string | undefined | null) => {
    if (!value) return value;
    return isNaLike(value) ? "na" : value;
  };

  // Strict transform: only converts na, NA, Na, nA.
  const strictNormalizeNa = (value: string | undefined | null) => {
    if (!value) return value;
    const lower = value.trim().toLowerCase();
    if (lower === "na") return "na";
    return value;
  };

  // Strict block for invalid NA variants (e.g. n/a, null, not available)
  const blockInvalidNaVariants = (value?: string | null) => {
    if (!value) return true;
    const lower = value.trim().toLowerCase();
    if (lower === "na") return true;
    if (["n/a", "not available", "null", "none", "nil"].includes(lower) || /^n\s*[\/\-\.]?\s*a\.?$/.test(lower)) {
      return false;
    }
    return true;
  };

  const formConfig = {
    pageName: "",
    initialValues,
    validationSchema: Yup.object().shape({
      certifiedOrganizationTeId: Yup.string()
        .required("TE-ID is required")
        .max(11, "Max 11 characters")
        .matches(/^TE-\d{8}$/, "Format TE-########"),
      certifiedOrganizationName: Yup.string()
        .transform(normalizeNaVariant)
        .required("Name is required")
        .max(255, "Max 255 characters")
        .test('no-whitespace', 'Whitespace-only value is not allowed', (v) => {
          if (!v) return true;
          return v.trim().length > 0;
        }),
      certifiedOrganizationCbClientNo: Yup.string()
        .required("Certified Organization CB Client No. is required")
        .max(50, "Max 50 characters")
        // M9: whitespace-only not allowed
        .test('no-whitespace', 'Whitespace-only value is not allowed in CB Client No.', (v) => {
          if (!v) return true;
          return v.trim().length > 0;
        })
        // CB Client Number must be a real value — "na", "n/a", "null", "none" etc. are all rejected
        .test('no-na-value', 'CB Client Number cannot be "na" or any NA variation. Please enter a valid client number.', (value) => {
          if (!value) return true; // handled by .required()
          return !isNaLike(value);
        }),
      certifiedOrganizationNameNative: Yup.string()
        .transform(strictNormalizeNa)
        .required()
        .max(255, "Max 255 characters")
        // M10: whitespace-only not allowed
        .test('no-whitespace', 'Whitespace-only value is not allowed in Native Name', (v) => {
          if (!v) return true;
          return v.trim().length > 0;
        })
        .test('no-invalid-na', 'Combinations of n/a, not available, or null are not allowed.', (v) => blockInvalidNaVariants(v))
        // M10: min-length check — <=6 chars rejected UNLESS exactly "na"
        // After transform, any NA variant is already "na" so the na exception always works
        .test('min-length-or-na', 'Values with 6 or fewer characters are not allowed (except "na")', (v) => {
          if (!v) return true;
          if (v.trim() === 'na') return true;
          return v.trim().length > 6;
        }),
      certifiedOrganizationAddress1: Yup.string()
        .transform(normalizeNaVariant)
        .required("Address is required")
        .max(1000, "Max 1000 characters")
        .test('no-whitespace', 'Whitespace-only value is not allowed', (v) => {
          if (!v) return true;
          return v.trim().length > 0;
        }),
      certifiedOrganizationTown: Yup.string()
        .transform(strictNormalizeNa)
        .max(255, "Max 255 characters")
        // M11: whitespace-only not allowed
        .test('no-whitespace', 'Whitespace-only value is not allowed in Town', (v) => {
          if (!v) return true;
          return v.trim().length > 0;
        })
        .test('no-invalid-na', 'Combinations of n/a, not available, or null are not allowed.', (v) => blockInvalidNaVariants(v)),
      certifiedOrganizationPostcode: Yup.string()
        .transform(strictNormalizeNa)
        .required()
        .max(50, "Max 50 characters")
        // M11: whitespace-only not allowed
        .test('no-whitespace', 'Whitespace-only value is not allowed in Postcode', (v) => {
          if (!v) return true;
          return v.trim().length > 0;
        })
        .test('no-invalid-na', 'Combinations of n/a, not available, or null are not allowed.', (v) => blockInvalidNaVariants(v)),
      certifiedOrganizationCountryOrArea: Yup.string().required(
        "Country/Area is required"
      ),
      certifiedOrganizationStateOrProvince: Yup.string().required(
        "State/Province is required"
      ),
      // certifiedOrganizationWebsite: Yup.string()
      //   .nullable()
      //   .matches(
      //     /^(?:(?:(?:https?:)?\/\/)?)?(?:www\.)?[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+(?:\/[^\s]*)?$/,
      //     "Please enter a valid website URL"
      //   ),
      // certifiedOrganizationEmail: Yup.array()
      //   .nullable()
      //   .of(
      //     Yup.string()
      //       .email("Please enter a valid email address")
      //       .matches(
      //         /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      //         "Please enter a valid email address"
      //       )
      //   ),
      // certifiedOrganizationContactLastName: Yup.string().nullable(),
      // certifiedOrganizationPublicEmail: Yup.string(),
    }),
    readOnly: hasSubmitSC,
    sections: [
      {
        title: "Fill the Certified Organization Information​", 
        fields: [
          {
            label: "Certified Organization TE-ID",
            name: "certifiedOrganizationTeId",
            type: "search",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.certifiedOrganizationTeId,
            visible: (value: any) => visible.certifiedOrganizationTEId,
            props: {
              placeholder: "Search TE-ID",
              variant: "outlined",
              param: "certifiedOrganizationTeId",
              restrictInputFormat: visible.coTEIdLabel,
              onChange: handleSellerTEIdValueChange,
              displayKey: "TE_ID",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "query");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "TE-ID",
            name: "certifiedOrganizationTeId",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            // value: 'initialValues.sellerAddress1',
            // disabled: (values: any) => disable.address1,
            visible: (value: any) => !visible.certifiedOrganizationTEId,
            props: {
              placeholder: "Enter TE-ID",
              fullWidth: true,
            },
          },
          {
            label: "Certified Organization Name",
            name: "certifiedOrganizationName",
            type: "search",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.certifiedOrganizationName,
            visible: (value: any) => visible.certifiedOrganizationName,
            props: {
              placeholder: "Search Name",
              variant: "outlined",
              param: "certifiedOrganizationName",
              onChange: handleSellerNameValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "name");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "Certified Organization Name",
            name: "certifiedOrganizationName",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            disabled: true,
            visible: (value: any) => !visible.certifiedOrganizationName,
            props: {
              placeholder: "Enter Name",
              fullWidth: true,
            },
          },
          {
            label: "Certified Organization Street Address",
            name: "certifiedOrganizationAddress1",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.certifiedOrganizationAddress1,
            // disabled: true,
            disabled: (values: any) => disable.address1,
            props: { placeholder: "Enter Address Line 1" },
          },
          {
            label: "Certified Organization Town",
            name: "certifiedOrganizationTown",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues?.certifiedOrganizationTown,
            helperText: 'Please fill "na" if no postcode is not applicable',
            disabled: (values: any) => disable.town,
            props: { placeholder: "Enter Town" },
          },
          {
            label: "Certified Organization Postcode",
            name: "certifiedOrganizationPostcode",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.certifiedOrganizationPostcode,
            disabled: (values: any) => disable.postcode,
            helperText: 'Please fill "na" if no postcode is not applicable',
            props: { placeholder: "Enter Postcode" },
          },
          {
            label: "Certified Organization Name Native",
            name: "certifiedOrganizationNameNative",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            helperText: 'Please fill "na" if no native name is not applicable',
            props: { placeholder: "Enter Native Name" },
          },
          {
            label: "Certified Organization Country/Area",
            name: "certifiedOrganizationCountryOrArea",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.certifiedOrganizationCountryOrArea,
            disabled: (values: any) => disable.country,
            events: {
              onChange: (newValue: any) => {
                let countryCode = JSON.stringify(
                  `[${[newValue].map((item) => `'${item}'`)}]`
                ).replace(/^"|"$/g, "");
                getState(countryCode);
              },
            },
            props: {
              enableSearch: true,
              options: countryOptions,
              placeholder: "Select Country/Area",
              fullWidth: true,
            },
          },
          {
            label: "Certified Organization State/Province",
            name: "certifiedOrganizationStateOrProvince",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.certifiedOrganizationStateOrProvince,
            disabled: (values: any) => disable.state,
            props: {
              options: stateOptions,
              enableSearch: true,
              placeholder: "Select State/Province",
              fullWidth: true,
              loading: isLoadingStates,
            },
          },
          {
            label: "Certified Organization CB Client Number",
            name: "certifiedOrganizationCbClientNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            props: {
              placeholder: "Enter Client Number of certified organization",
            },
          },
          // {
          //   label: "Contact Person First Name",
          //   name: "certifiedOrganizationContactFirstName",
          //   type: "text",
          //   gridSize: { xs: 12, md: 4, lg: 4 },
          //   required: false,
          //   props: { placeholder: "Enter Contact Person First Name" },
          // },
          // {
          //   label: "Contact Person Last Name",
          //   name: "certifiedOrganizationContactLastName",
          //   type: "text",
          //   gridSize: { xs: 12, md: 4, lg: 4 },
          //   required: false,
          //   props: { placeholder: "Enter Contact Person Last Name" },
          // },
          // {
          //   label: "Website",
          //   name: "certifiedOrganizationWebsite",
          //   type: "text",
          //   gridSize: { xs: 12, md: 4, lg: 4 },
          //   required: false,
          //   props: { placeholder: "Enter Website" },
          // },
          // {
          //   label: "Public Email",
          //   name: "certifiedOrganizationEmail",
          //   type: "chips",
          //   gridSize: { xs: 12, md: 12, lg: 12 },
          //   required: false,
          //   props: {
          //     placeholder: "Enter Email",
          //     fullWidth: true,
          //   },
          // },
          // {
          //   label: "Display Certified org public email on Public Listing",
          //   name: "certifiedOrganizationPublicEmail",
          //   type: "radio",
          //   gridSize: { xs: 12, md: 12, lg: 12 }, // Changed to full width
          //   required: false,
          //   value: initialValues.certifiedOrganizationPublicEmail,
          //   props: {
          //     options: [
          //       { value: "Y", label: "Yes" },
          //       { value: "N", label: "No" },
          //     ],
          //   },
          // },
        ],
      },
    ],
    handleSubmit: async (values: any) => {
      try {
        if (SC_ID) {
          setLoadingState(true);
          // Normalize ALL NA variants → 'na' before saving — uses isNaLike which covers
          // 'n/a', 'N/A', 'null', 'not available', 'nil', 'none', 'n.a', 'n a', etc.
          // (CB Client No is excluded — NA is not allowed there at all)
          const normalizeNa = (v?: string) =>
            v && isNaLike(v.trim()) ? 'na' : (v || '');
          const normalizedValues = {
            ...values,
            certifiedOrganizationName: normalizeNa(values.certifiedOrganizationName),
            certifiedOrganizationNameNative: normalizeNa(values.certifiedOrganizationNameNative),
            certifiedOrganizationAddress1: normalizeNa(values.certifiedOrganizationAddress1),
            certifiedOrganizationTown: normalizeNa(values.certifiedOrganizationTown),
            certifiedOrganizationPostcode: normalizeNa(values.certifiedOrganizationPostcode),
          };
          const payload = mapPayload(
            normalizedValues,
            SC_ID,
            countryOptions,
            stateOptions
          );
          const result: any = await executePost(API_ROUTES.CO_DETAILS_V4, payload);
          if (result?.status == 200) {
            notify("Saved Succesfully", "success");
            await onNextTab();
            navigate(`/v4/sc/${formtype}/facility-details/${SC_ID}`);
          } else {
            notify("Unable to Save", "error");
            setLoadingState(false);
          }
        }
      } catch (error) {
        setLoadingState(false);
        console.error("Error saving Certified Organization Details:", error);
      }
    },
  };

  // Handle form submission outside of FormBuilderV4
  const handleSubmit = async () => {
    if (formRef.current) {
      const formik = formRef.current;
      // submitForm() always increments Formik's submitCount even when validation fails.
      // This is the trigger that makes hasError=true for all invalid fields (red highlight + error text).
      // If validation passes, it also calls the formConfig.handleSubmit which saves + navigates.
      await formik.submitForm();
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        notify("Please fill in all required fields before proceeding", "error");
      }
    }
  };

  const handleNavigateToNextTab = () => {
    navigate(`/v4/sc/${formtype}/facility-details/${SC_ID}`);
  };

  return (
    <Box>
      {/* Render the FormBuilderV4 component and pass formRef and onSubmit handler */}
      {loading ? (
        <LoaderComponent />
      ) : (
        <>
          <FormBuilderV4
            formConfig={formConfig}
            onSubmit={formConfig.handleSubmit}
            formRef={formRef}
          />

          {/* Submit button outside the FormBuilderV4 */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <NavLink to="/v4/sc-dashboard">
              <StyledButton sx={{ boxShadow: "none" }}>
                Home
              </StyledButton>
            </NavLink>
            <StyledButton
              variant="contained"
              sx={{ boxShadow: "none" }}
              onClick={hasSubmitSC ? handleNavigateToNextTab : handleSubmit}
            >
              {hasSubmitSC ? "Next" : "Save & Next"}
            </StyledButton>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CertifiedOrganizationDetails;
